const cipherHelpText = `
    The following represents a list of valid instance names necessary for generation of a developer kit or a uniquely seeded production kit.

    A valid instance begins with "i" followed by the algorithm name. Consult the list below and provide the instances you expect to find in your development or production kit (one instance per line).

    Note that this does not represent an exhaustive list of instances.
    White-Box Cryptography Protection (TransformIT) can include other (custom) instances based on color names, key size, etc.
    Custom/additional instances can be specified based on the following structure:

    Algorithm: [iRSA, iAES, etc.]
    Key size: [128, 256, etc.]
    Input color: [None, Red, Green, etc.]
    Output color: [None, Red, Green, etc.]
    Direction: [(E)ncrypt, (D)ecrypt, (S)ign, (V)erify, (I)nput, (O)output, (K)ey, (M)message, etc.]

    Consult the Installation Guide for more information on custom instance names, including possible component identifiers.

    Asymmetric key algorithms
        RSA (Rivest-Shamir-Adleman) instances
            iRSADMDMGF256OAEP2048greenK
            iRSADMDMGF256OAEP2048greenKgreenPT
            iRSADMDMGF256OAEP2048greenKredPT
            iRSADMDMGF256OAEP2048redCTgreenKgreenPT
            iRSADMDOAEP2048greenCTgreenKredPT
            iRSADMDOAEP2048greenK
            iRSADMDOAEP2048greenKredPT
            iRSADMDPKCS2048greenCTgreenKredPT
            iRSADMDPKCS2048greenK
            iRSADMDPKCS2048greenKredPT
            iRSADMDUSER2048greenCTgreenKredPT
            iRSADMDUSER2048greenK
            iRSADMDUSER2048greenKredPT
            iRSADMGF256OAEP2048
            iRSADMGF256OAEP2048greenPT
            iRSADMGF256OAEP2048redCTgreenPT
            iRSADMGF256OAEP2048redPT
            iRSADMSEMSAPSS2048greenK
            iRSADMSEMSAPSS2048redMgreenKgreenS
            iRSADMSPKCS2048greenK
            iRSADMSPKCS2048redMgreenKgreenS
            iRSADMSsha3EMSAPSS2048greenMredKorangeS
            iRSADMSsha3EMSAPSS2048redK
            iRSADMSsha3PKCS2048greenMredKorangeS
            iRSADMSsha3PKCS2048redK
            iRSADOAEP2048
            iRSADOAEP2048greenCTredPT
            iRSADOAEP2048redPT
            iRSADPKCS2048
            iRSADPKCS2048greenCTredPT
            iRSADPKCS2048redPT
            iRSADUSER2048
            iRSADUSER2048greenCTredPT
            iRSADUSER2048redPT
            iRSAEMGF256OAEP2048redPTgreenCT
            iRSAEOAEP2048redPTgreenCT
            iRSAEPKCS2048redPTgreenCT
            iRSAEUSER2048redPTgreenCT
            iRSASEMSAPSS2048
            iRSASEMSAPSS2048redMgreenS
            iRSASPKCS2048
            iRSASPKCS2048redMgreenS
            iRSASsha3EMSAPSS2048
            iRSASsha3EMSAPSS2048greenMorangeS
            iRSASsha3PKCS2048
            iRSASsha3PKCS2048greenMorangeS
            iRSAVEMSAPSS2048redMgreenS
            iRSAVPKCS2048redMgreenS
            iRSAVsha3EMSAPSS2048greenMorangeS
            iRSAVsha3PKCS2048greenMorangeS

    Symmetric encryption algorithms
        Advanced Encryption Standard (AES) instances
            iAES1greenPT
            iAES1redPT
            iAES2
            iAES21
            iAES21greenPT
            iAES21redPT
            iAES22
            iAES22greenPT
            iAES22orangePT
            iAES22redPT
            iAES23
            iAES23CFIVgreenPT
            iAES23CFIVredPT
            iAES23greenPT
            iAES23greenPTorangeIV
            iAES23redPT
            iAES23redPTorangeIV
            iAES24
            iAES24CFIVgreenPT
            iAES24CFIVredPT
            iAES24greenPT
            iAES24orangeIVgreenPT
            iAES24orangeIVredPT
            iAES24redPT
            iAES25
            iAES26
            iAES26greenPT
            iAES26redPT
            iAES27
            iAES27greenPT
            iAES27redPT
            iAES28
            iAES28greenPT
            iAES28redPT
            iAES29
            iAES29greenPT
            iAES29redPT
            iAES2greenPT
            iAES2orangePT
            iAES2redPT
            iAES3
            iAES3CFIVgreenPT
            iAES3CFIVredPT
            iAES3greenPT
            iAES3greenPTorangeIV
            iAES3redPT
            iAES3redPTorangeIV
            iAES4
            iAES4CFIVgreenPT
            iAES4CFIVredPT
            iAES4greenPT
            iAES4orangeIVgreenPT
            iAES4orangeIVredPT
            iAES4redPT
            iAES5
            iAES6
            iAES6greenPT
            iAES6redPT
            iAES7
            iAES7greenPT
            iAES7redPT
            iAES8
            iAES8greenPT
            iAES8redPT
            iAES9
            iAES9greenPT
            iAES9redPT

    Key wrap algorithms
        NISt key wrap instances
            iNWRAP1
            iNWRAP11
            iNWRAP12
            iNWRAP12AESredM
            iNWRAP12ECCcopperM
            iNWRAP2
            iNWRAP21
            iNWRAP22
            iNWRAP22AESredM
            iNWRAP22ECCcopperM
            iNWRAP2AESredM
            iNWRAP2ECCcopperM

    Key derivation algorithms
        NIST KDF instances
            iNKDF128greenOK
            iNKDF128redOK
            iNKDF192greenOK
            iNKDF192redOK
            iNKDF256greenOK
            iNKDF256redOK

        OMA key derivation instances
            iOKDFcherrySlimeOK
            iOKDFlimeScherryOK

    Integrity and authenticity
        CMAC (Cipher-based Message Authentication Code) instances
            iCMAC
            iCMACgreenMredD
            iCMACredMgreenD

        HMAC (Hash-based Message Authentication Code) instances
            iHMAC
            iHMACgreenI
            iHMACgreenIredO
            iHMACgreenO
            iHMACredI
            iHMACredIgreenO
            iHMACredO

        MAC (Message Authentication Code) instances
            iMAC3
            iMAC3copperD
            iMAC3steelM
            iMAC3steelMcopperD

    Elliptic curve cryptography
        Elliptic Curve Diffie-Hellman (ECDH) instances
            iECDHgreenKredSS
            iECDHgreenSSfastP256
            iECDHredKgreenSS
            iECDHredSSfastP256

        Elliptic Curve Digital Signature Algorithm Instances
            iECDSASfastP256
            iECDSASfastP384
            iECDSASgreenK
            iECDSASredK
            iECDSAVfastP256
            iECDSAVfastP384
            iECDSAVgreenK
            iECDSAVredK

    Other elliptic curve cryptography
        Elliptic Curve ElGamal instances
            iECEGDcherryPTfastP256
            iECEGDfastP256
            iECEGDgreenK
            iECEGDgreenKcherryPT
            iECEGDlimePTfastP256
            iECEGDredK
            iECEGDredKlimePT
            iECEGEcherryPTfastP256
            iECEGEfastP256
            iECEGEgreenK
            iECEGEgreenKcherryPT
            iECEGElimePTfastP256
            iECEGEredK
            iECEGEredKlimePT

        Curve25519 instances
            Curve25519-donna

        Elliptic Curve Toolkit (ECTK) instances
            iECTKGP256greenK
            iECTKGP256greenKgreenO
            iECTKGP256greenKredO
            iECTKGP256redK
            iECTKGP256redKgreenO
            iECTKGP256redKredO
            iECTKP256greenK
            iECTKP256greenKgreenIgreenO
            iECTKP256greenKgreenIredO
            iECTKP256greenKgreenO
            iECTKP256greenKredIgreenO
            iECTKP256greenKredIredO
            iECTKP256greenKredO
            iECTKP256redK
            iECTKP256redKgreenIgreenO
            iECTKP256redKgreenIredO
            iECTKP256redKgreenO
            iECTKP256redKredIgreenO
            iECTKP256redKredIredO
            iECTKP256redKredO

    Secure hash algorithms
        SHA-3 and SHAKE (Secure Hash Algorithm KECCAK) instances
            iSHA3copperM
            iSHA3copperMcopperD
            iSHA3copperMsteelD
            iSHA3steelM
            iSHA3steelMcopperD
            iSHA3steelMsteelD
            iSHAKEcopperI
            iSHAKEcopperIcopperO
            iSHAKEcopperIsteelO
            iSHAKEsteelI
            iSHAKEsteelIcopperO
            iSHAKEsteelIsteelO

        SHA instances
            iSHAredM
            iSHAredMgreenD
            iSHAgreenM
            iSHAgreenMredD

    Split knowledge algorithms
        Shamir's Secret Sharing instances
            iSSCgreenIredO
            iSSCredIgreenO
            iSSSgreenO
            iSSSredO

    Other algorithms
        DES (Data Encryption Standard) instances
            iDES1
            iDES1copperCT
            iDES1copperPT
            iDES1copperPTsteelCT
            iDES1steelCT
            iDES1steelPT
            iDES1steelPTcopperCT
            iDES2
            iDES2copperCT
            iDES2copperCTsteelPT
            iDES2copperPT
            iDES2steelCT
            iDES2steelCTcopperPT
            iDES2steelPT

        3DES (Triple Data Encryption Standard) instances
            i3DES1
            i3DES1copperCT
            i3DES1copperPT
            i3DES1copperPTsteelCT
            i3DES1steelCT
            i3DES1steelPT
            i3DES1steelPTcopperCT
            i3DES2
            i3DES2copperCT
            i3DES2copperCTsteelPT
            i3DES2copperPT
            i3DES2steelCT
            i3DES2steelCTcopperPT
            i3DES2steelPT

        Big integer instances
            iBIgreenI
            iBIgreenIgreenO
            iBIgreenIredO
            iBIredI
            iBIredIgreenO
            iBIredIredO

        Linear algebra instances
            iLAgreenIredO
            iLAredIgreenO

        Finite Field Diffie-Hellman instances
            iFFDHgreenSS
            iFFDHredSS

        XLAT XOR instances
            iXORgreenXIgreenXMgreenXO
            iXORgreenXIgreenXMredXO
            iXORgreenXIredXMredXO
            iXORredXIgreenXM
            iXORredXIgreenXMgreenXO
            iXORredXIredXMgreenXO
            iXORredXIredXMredXO`;

export default cipherHelpText;
